import { useState } from 'react';
import { motion } from 'framer-motion';
import { FolderKanban, Plus, Search, Calendar, User, X, CheckCircle } from 'lucide-react';
import { useAdminStore } from '../store/adminStore';
import { formatDate, getStatusColor, getStatusLabel, generateId } from '../utils/helpers';
import type { Project } from '../types';

const statusOrder = ['site_visit', 'quotation', 'approved', 'installation', 'completed', 'warranty'] as const;

// Simple inline toast (no external dep)
function Toast({ message, type, onClose }: { message: string; type: 'success' | 'error'; onClose: () => void }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      className={`fixed top-5 right-5 z-[100] flex items-center gap-3 px-5 py-3 rounded-2xl shadow-lg text-[13px] font-medium ${
        type === 'success' ? 'bg-emerald-600 text-white' : 'bg-red-600 text-white'
      }`}
    >
      <CheckCircle className="w-4 h-4" />
      {message}
      <button onClick={onClose} className="ml-2 opacity-75 hover:opacity-100"><X className="w-3.5 h-3.5" /></button>
    </motion.div>
  );
}

const BLANK_PROJECT = {
  name: '',
  customerId: '',
  customerName: '',
  status: 'site_visit' as Project['status'],
  technicianId: '',
  technicianName: '',
  estimatedDate: '',
  notes: '',
};

export default function ProjectsPage() {
  const { projects, customers, staff, addProject, updateProject, deleteProject } = useAdminStore();
  const [search, setSearch] = useState('');
  const [view, setView] = useState<'board' | 'list'>('board');
  const [showAdd, setShowAdd] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState({ ...BLANK_PROJECT });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const filtered = projects.filter(p =>
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    p.customerName.toLowerCase().includes(search.toLowerCase())
  );

  const grouped = statusOrder.reduce((acc, status) => {
    acc[status] = filtered.filter(p => p.status === status);
    return acc;
  }, {} as Record<string, typeof projects>);

  const validateForm = () => {
    const e: Record<string, string> = {};
    if (!form.name.trim()) e.name = 'Project name is required';
    if (!form.customerName.trim() && !form.customerId) e.customerName = 'Customer is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const openAdd = () => {
    setForm({ ...BLANK_PROJECT });
    setErrors({});
    setEditId(null);
    setShowAdd(true);
  };

  const openEdit = (project: Project) => {
    setForm({
      name: project.name,
      customerId: project.customerId,
      customerName: project.customerName,
      status: project.status,
      technicianId: project.technicianId || '',
      technicianName: project.technicianName || '',
      estimatedDate: project.estimatedDate ? project.estimatedDate.slice(0, 10) : '',
      notes: project.notes || '',
    });
    setErrors({});
    setEditId(project.id);
    setShowAdd(true);
  };

  const handleCustomerChange = (id: string) => {
    const cust = customers.find(c => c.id === id);
    setForm(f => ({ ...f, customerId: id, customerName: cust?.name || id }));
  };

  const handleTechChange = (id: string) => {
    const tech = staff.find(s => s.id === id);
    setForm(f => ({ ...f, technicianId: id, technicianName: tech?.name || '' }));
  };

  const handleSave = () => {
    if (!validateForm()) return;

    if (editId) {
      updateProject(editId, {
        name: form.name,
        customerId: form.customerId,
        customerName: form.customerName,
        status: form.status,
        technicianId: form.technicianId || undefined,
        technicianName: form.technicianName || undefined,
        estimatedDate: form.estimatedDate || undefined,
        notes: form.notes || undefined,
      });
      showToast('Project updated successfully');
    } else {
      const newProject: Project = {
        id: generateId(),
        name: form.name,
        customerId: form.customerId,
        customerName: form.customerName,
        status: form.status,
        technicianId: form.technicianId || undefined,
        technicianName: form.technicianName || undefined,
        estimatedDate: form.estimatedDate || undefined,
        notes: form.notes || undefined,
        createdAt: new Date().toISOString(),
      };
      addProject(newProject);
      showToast('Project created successfully');
    }
    setShowAdd(false);
  };

  const handleDelete = (id: string, name: string) => {
    if (!window.confirm(`Delete project "${name}"?`)) return;
    deleteProject(id);
    showToast('Project deleted', 'error');
  };

  const handleStatusChange = (projectId: string, newStatus: Project['status']) => {
    updateProject(projectId, { status: newStatus });
    showToast(`Status updated to ${getStatusLabel(newStatus)}`);
  };

  const activeStaff = staff.filter(s => s.isActive);

  return (
    <div className="space-y-6">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-[24px] font-semibold text-gray-900 tracking-tight">Projects</h1>
          <p className="text-[13px] text-gray-400 mt-0.5">{projects.length} total projects</p>
        </div>
        <div className="flex gap-2">
          <div className="flex bg-white border border-gray-200/60 rounded-xl overflow-hidden">
            <button onClick={() => setView('board')} className={`px-3 h-10 text-[12px] font-medium ${view === 'board' ? 'bg-gray-900 text-white' : 'text-gray-500'}`}>Board</button>
            <button onClick={() => setView('list')} className={`px-3 h-10 text-[12px] font-medium ${view === 'list' ? 'bg-gray-900 text-white' : 'text-gray-500'}`}>List</button>
          </div>
          <button onClick={openAdd} className="h-10 px-5 rounded-xl bg-gray-900 text-white text-[13px] font-medium hover:bg-gray-800 flex items-center gap-2">
            <Plus className="w-4 h-4" />New Project
          </button>
        </div>
      </div>

      <div className="relative">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search projects..."
          className="w-full h-10 pl-10 pr-4 rounded-xl bg-white border border-gray-200/60 text-[13px] focus:outline-none focus:ring-2 focus:ring-gray-900/5" />
      </div>

      {view === 'board' ? (
        <div className="flex gap-4 overflow-x-auto pb-4">
          {statusOrder.map(status => (
            <div key={status} className="flex-shrink-0 w-72">
              <div className="flex items-center gap-2 mb-3 px-1">
                <span className={`w-2 h-2 rounded-full ${status === 'completed' ? 'bg-emerald-400' : status === 'installation' ? 'bg-amber-400' : 'bg-blue-400'}`} />
                <span className="text-[12px] font-semibold text-gray-500 uppercase tracking-wider">{getStatusLabel(status)}</span>
                <span className="text-[11px] text-gray-400 ml-auto">{grouped[status]?.length || 0}</span>
              </div>
              <div className="space-y-2 min-h-[200px]">
                {(grouped[status] || []).map(project => (
                  <motion.div key={project.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                    className="bg-white/80 backdrop-blur-xl rounded-2xl border border-gray-200/60 p-4 hover:shadow-lg hover:shadow-gray-200/40 transition-all cursor-pointer">
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="text-[13px] font-semibold text-gray-900 flex-1">{project.name}</h4>
                      <div className="flex gap-1 ml-2">
                        <button onClick={() => openEdit(project)} className="p-1 rounded hover:bg-gray-100 text-gray-400 text-[10px]">✏</button>
                        <button onClick={() => handleDelete(project.id, project.name)} className="p-1 rounded hover:bg-red-50 text-red-400 text-[10px]">✕</button>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-[11px] text-gray-400 mb-2">
                      <User className="w-3 h-3" />{project.customerName}
                    </div>
                    {project.technicianName && (
                      <div className="flex items-center gap-2 text-[11px] text-gray-400 mb-2">
                        <FolderKanban className="w-3 h-3" />{project.technicianName}
                      </div>
                    )}
                    {project.estimatedDate && (
                      <div className="flex items-center gap-2 text-[11px] text-gray-400 mb-3">
                        <Calendar className="w-3 h-3" />{formatDate(project.estimatedDate)}
                      </div>
                    )}
                    {/* Quick status change */}
                    <select
                      value={project.status}
                      onChange={e => handleStatusChange(project.id, e.target.value as Project['status'])}
                      onClick={e => e.stopPropagation()}
                      className="w-full text-[10px] border border-gray-200 rounded-lg px-2 py-1 bg-gray-50 focus:outline-none"
                    >
                      {statusOrder.map(s => <option key={s} value={s}>{getStatusLabel(s)}</option>)}
                    </select>
                  </motion.div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white/80 backdrop-blur-xl rounded-2xl border border-gray-200/60 overflow-hidden">
          <table className="w-full">
            <thead><tr className="border-b border-gray-100">
              <th className="text-left px-5 py-3 text-[11px] font-semibold text-gray-400 uppercase">Project</th>
              <th className="text-left px-5 py-3 text-[11px] font-semibold text-gray-400 uppercase">Customer</th>
              <th className="text-left px-5 py-3 text-[11px] font-semibold text-gray-400 uppercase">Status</th>
              <th className="text-left px-5 py-3 text-[11px] font-semibold text-gray-400 uppercase">Technician</th>
              <th className="text-left px-5 py-3 text-[11px] font-semibold text-gray-400 uppercase">Date</th>
              <th className="text-left px-5 py-3 text-[11px] font-semibold text-gray-400 uppercase">Actions</th>
            </tr></thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="px-5 py-8 text-center text-[13px] text-gray-400">No projects found</td></tr>
              )}
              {filtered.map(p => (
                <tr key={p.id} className="border-b border-gray-50 hover:bg-gray-50/50">
                  <td className="px-5 py-3 text-[13px] font-medium text-gray-900">{p.name}</td>
                  <td className="px-5 py-3 text-[13px] text-gray-600">{p.customerName}</td>
                  <td className="px-5 py-3">
                    <select
                      value={p.status}
                      onChange={e => handleStatusChange(p.id, e.target.value as Project['status'])}
                      className="text-[11px] border border-gray-200 rounded-lg px-2 py-1 bg-gray-50 focus:outline-none"
                    >
                      {statusOrder.map(s => <option key={s} value={s}>{getStatusLabel(s)}</option>)}
                    </select>
                  </td>
                  <td className="px-5 py-3 text-[13px] text-gray-600">{p.technicianName || '—'}</td>
                  <td className="px-5 py-3 text-[12px] text-gray-400">{p.estimatedDate ? formatDate(p.estimatedDate) : formatDate(p.createdAt)}</td>
                  <td className="px-5 py-3 flex gap-2">
                    <button onClick={() => openEdit(p)} className="text-[11px] px-2 py-1 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100">Edit</button>
                    <button onClick={() => handleDelete(p.id, p.name)} className="text-[11px] px-2 py-1 rounded-lg bg-red-50 text-red-600 hover:bg-red-100">Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showAdd && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowAdd(false)}>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            className="w-full max-w-lg bg-white rounded-3xl shadow-2xl border border-gray-200/60 p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-[18px] font-semibold text-gray-900">{editId ? 'Edit Project' : 'New Project'}</h2>
              <button onClick={() => setShowAdd(false)} className="p-1.5 rounded-xl hover:bg-gray-100"><X className="w-5 h-5 text-gray-400" /></button>
            </div>
            <div className="space-y-4">
              {/* Project Name */}
              <div>
                <label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Project Name *</label>
                <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Solar Installation - Colombo 7"
                  className={`w-full h-10 px-3 rounded-xl bg-gray-50 border text-[13px] focus:outline-none focus:ring-2 focus:ring-gray-900/5 ${errors.name ? 'border-red-300' : 'border-gray-200/60'}`} />
                {errors.name && <p className="text-red-500 text-[11px] mt-1">{errors.name}</p>}
              </div>

              {/* Customer */}
              <div>
                <label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Customer *</label>
                <select value={form.customerId || ''} onChange={e => handleCustomerChange(e.target.value)}
                  className={`w-full h-10 px-3 rounded-xl bg-gray-50 border text-[13px] focus:outline-none focus:ring-2 focus:ring-gray-900/5 ${errors.customerName ? 'border-red-300' : 'border-gray-200/60'}`}>
                  <option value="">Select customer…</option>
                  {customers.map(c => <option key={c.id} value={c.id}>{c.name} — {c.mobile}</option>)}
                </select>
                {errors.customerName && <p className="text-red-500 text-[11px] mt-1">{errors.customerName}</p>}
              </div>

              {/* Technician + Status */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Assign Technician</label>
                  <select value={form.technicianId || ''} onChange={e => handleTechChange(e.target.value)}
                    className="w-full h-10 px-3 rounded-xl bg-gray-50 border border-gray-200/60 text-[13px] focus:outline-none">
                    <option value="">Unassigned</option>
                    {activeStaff.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Status</label>
                  <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value as Project['status'] }))}
                    className="w-full h-10 px-3 rounded-xl bg-gray-50 border border-gray-200/60 text-[13px] focus:outline-none">
                    {statusOrder.map(s => <option key={s} value={s}>{getStatusLabel(s)}</option>)}
                  </select>
                </div>
              </div>

              {/* Estimated Date */}
              <div>
                <label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Estimated Date</label>
                <input type="date" value={form.estimatedDate} onChange={e => setForm(f => ({ ...f, estimatedDate: e.target.value }))}
                  className="w-full h-10 px-3 rounded-xl bg-gray-50 border border-gray-200/60 text-[13px] focus:outline-none focus:ring-2 focus:ring-gray-900/5" />
              </div>

              {/* Notes */}
              <div>
                <label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Notes</label>
                <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2}
                  placeholder="Optional notes…"
                  className="w-full px-3 py-2 rounded-xl bg-gray-50 border border-gray-200/60 text-[13px] focus:outline-none focus:ring-2 focus:ring-gray-900/5 resize-none" />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setShowAdd(false)} className="h-10 px-5 rounded-xl bg-gray-100 text-gray-600 text-[13px] font-medium hover:bg-gray-200">Cancel</button>
              <button onClick={handleSave} className="h-10 px-5 rounded-xl bg-gray-900 text-white text-[13px] font-medium hover:bg-gray-800">
                {editId ? 'Update Project' : 'Create Project'}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
